<script setup lang="ts">
import { onBeforeUnmount, useId } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { PopoverClose } from 'reka-ui';
import { appOrigin } from './navigation';
import type { PlatformTool } from './login-platform';

defineProps<{ tool: PlatformTool; active: boolean; open: boolean; mobile: boolean }>();
const emit = defineEmits<{ 'update:open': [open: boolean] }>();
const headingId = useId();
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

function hideCancel() {
  if (hideTimeout) clearTimeout(hideTimeout);
  hideTimeout = null;
}
function show() {
  hideCancel();
  emit('update:open', true);
}
function pointerShow(event: PointerEvent) {
  if (event.pointerType === 'mouse') show();
}
function focusShow(event: FocusEvent) {
  if ((event.target as HTMLElement).matches(':focus-visible')) show();
}
function pointerHide(event: PointerEvent) {
  if (event.pointerType === 'mouse') hideSoon();
}
function hideSoon() {
  hideCancel();
  hideTimeout = setTimeout(() => emit('update:open', false), 250);
}
onBeforeUnmount(hideCancel);
</script>

<template>
  <Popover :open="open" @update:open="emit('update:open', $event)">
    <PopoverTrigger asChild @pointerenter="pointerShow" @pointerleave="pointerHide" @focus="focusShow" @blur="hideSoon">
      <slot />
    </PopoverTrigger>
    <PopoverContent
      asChild
      :side="mobile ? 'bottom' : 'right'"
      align="start"
      :sideOffset="mobile ? 10 : -10"
      :alignOffset="mobile ? 0 : 10"
      :collisionPadding="16"
      @openAutoFocus.prevent
      @closeAutoFocus.prevent
      @pointerenter="hideCancel"
      @pointerleave="pointerHide"
      @focusin="hideCancel"
      @focusout="hideSoon"
    >
      <div class="tool-popover" :aria-labelledby="headingId">
        <PopoverClose class="popover-close" aria-label="Close tool details">×</PopoverClose>
        <b :id="headingId">{{ tool.name }}</b>
        <p>{{ tool.description }}</p>
        <a
          v-if="tool.available"
          :href="active ? '#sign-in' : `${appOrigin(tool.app)}/login`"
          class="popover-action"
          @click="emit('update:open', false)"
        >
          <i aria-hidden="true"></i>
          {{ active ? 'Sign in here' : `Sign in to ${tool.label}` }} <span aria-hidden="true">→</span>
        </a>
        <a
          v-else-if="tool.app === null"
          href="https://www.envirotoolkit.com/contact"
          class="popover-action"
          @click="emit('update:open', false)"
        >
          <i aria-hidden="true"></i>
          Contact us <span aria-hidden="true">→</span>
        </a>
        <p v-else class="popover-status">
          <i aria-hidden="true"></i>
          Coming soon
        </p>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped>
.tool-popover {
  width: 300px;
  max-width: calc(100vw - 32px);
  padding: 16px 18px;
  border: 1px solid rgb(8 24 72 / 12%);
  border-radius: 12px;
  background: #fff;
  color: var(--color-brand-blue);
  box-shadow: 0 12px 36px rgb(0 0 0 / 25%);
  font-family: system-ui, 'Segoe UI', sans-serif;
  font-size: 15px;
  line-height: 1.4;
}
.tool-popover b {
  display: block;
  padding-right: 20px;
  font-size: 18px;
  font-weight: 700;
}
.tool-popover p {
  margin: 8px 0 0;
}
.popover-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 22px;
}
.popover-close:hover {
  background: rgb(8 24 72 / 7%);
}
.popover-action,
.tool-popover .popover-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.popover-action {
  color: inherit;
  font-weight: 600;
  text-decoration: none;
}
.popover-action:hover {
  text-decoration: underline;
}
.popover-action span {
  margin-left: auto;
}
.popover-action i,
.popover-status i {
  flex: 0 0 8px;
  height: 8px;
  border-radius: 50%;
  background: #0aaa70;
}
.popover-status i {
  background: #ffc400;
}
.popover-action:focus-visible,
.popover-close:focus-visible {
  outline: 2px solid var(--color-brand-blue);
  outline-offset: 3px;
}
@media (prefers-reduced-motion: reduce) {
  .tool-popover {
    animation: none;
  }
}
</style>
